import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';

import {
	pluginKey,
	type AgentManagedBlockLifecycleState,
	type AgentManagedExtensionPluginState,
} from '../../common/agentManagedExtensionPluginType';
import {
	findAgentManagedExtension,
	findAgentManagedExtensionByLocalId,
	isAgentManagedExtension,
} from '../../common/utils';
import {
	cacheAiManagedObjectInPluginState,
	getAiManagedObjectLoadStatusByLocalIdFromPluginState,
	getAiManagedObjectLoadStatusFromPluginState,
	getAiManagedObjectLoadStatusFromState,
	getCachedAiManagedObjectByLocalIdFromPluginState,
	getCachedAiManagedObjectFromPluginState,
	getCachedAiManagedObjectFromState,
	setAiManagedObjectLoadStatusInPluginState,
} from './ai-managed-object-state';

type CachedAiManagedObject = AgentManagedExtensionPluginState['aiManagedObjectsById'][string];

// eslint-disable-next-line import/order -- AFM runs conflicting import group checks for this re-export block.
export {
	getAiManagedObjectLoadStatusByLocalIdFromPluginState,
	getAiManagedObjectLoadStatusFromPluginState,
	getAiManagedObjectLoadStatusFromState,
	getCachedAiManagedObjectByLocalIdFromPluginState,
	getCachedAiManagedObjectFromPluginState,
	getCachedAiManagedObjectFromState,
};

export type AgentManagedExtensionMetaAction =
	| { localId?: string; type: 'OPEN_CONFIG' }
	| { type: 'CLOSE_CONFIG' }
	| { type: 'TOGGLE_CONFIG' }
	| { localId: string; requestId: string; type: 'FAIL_EXECUTION' }
	| {
			lifecycleState: AgentManagedBlockLifecycleState;
			localId: string;
			requestId: string;
			type: 'START_EXECUTION';
	  }
	| { localId: string; requestId: string; type: 'FINISH_EXECUTION' }
	| {
			aiManagedObject: CachedAiManagedObject;
			localId?: string;
			type: 'CACHE_AI_MANAGED_OBJECT';
	  }
	| { localId?: string; type: 'OPEN_AUTO_UPDATE_INTERVAL' }
	| { type: 'CLOSE_AUTO_UPDATE_INTERVAL' }
	| { localId?: string; type: 'TOGGLE_AUTO_UPDATE_INTERVAL' }
	| { aimoId: string; localId?: string; type: 'FAIL_AIMO_LOAD' }
	| { aimoId: string; localId?: string; type: 'START_AIMO_LOAD' };

export type AgentManagedExtensionMeta =
	| AgentManagedExtensionMetaAction
	| readonly AgentManagedExtensionMetaAction[];

const META_KEY = 'agentManagedExtension';

export const setMeta = (tr: Transaction, meta: AgentManagedExtensionMeta): Transaction =>
	tr.setMeta(META_KEY, meta);

export const getMeta = (
	tr: Transaction | ReadonlyTransaction,
): AgentManagedExtensionMeta | undefined =>
	tr.getMeta(META_KEY) as AgentManagedExtensionMeta | undefined;

export const initialState = (): AgentManagedExtensionPluginState => ({
	aiManagedObjectsById: {},
	aiManagedObjectIdsByLocalId: {},
	aiManagedObjectLoadStatusById: {},
	executionErrorLocalIds: {},
	executionLifecycleByLocalId: {},
	isAutoUpdateIntervalOpen: false,
	isConfigOpen: false,
});

export const getIsConfigOpenFromState = (state: EditorState): boolean =>
	pluginKey.getState(state)?.isConfigOpen ?? false;

export const getAutoUpdateIntervalTargetFromState = (
	state: EditorState,
): { isOpen: boolean; localId?: string } => {
	const pluginState = pluginKey.getState(state);
	return {
		isOpen: pluginState?.isAutoUpdateIntervalOpen ?? false,
		localId: pluginState?.autoUpdateIntervalLocalId,
	};
};

const collectLocalIds = (doc: EditorState['doc']): Set<string> => {
	const found = new Set<string>();
	doc.descendants((node) => {
		if (!isAgentManagedExtension(node)) {
			return true;
		}
		const localId = node.attrs.localId as string | undefined;
		if (localId) {
			found.add(localId);
		}
		return false;
	});
	return found;
};

const findNewLocalIds = (oldState: EditorState, newState: EditorState): string[] => {
	const oldLocalIds = collectLocalIds(oldState.doc);
	const newLocalIds = collectLocalIds(newState.doc);
	return [...newLocalIds].filter((localId) => !oldLocalIds.has(localId));
};

const isPasteTransaction = (tr: Transaction | ReadonlyTransaction): boolean =>
	Boolean(tr.getMeta('paste') ?? tr.getMeta('uiEvent') === 'paste');

const isLocalUserInsertTransaction = (tr: Transaction | ReadonlyTransaction): boolean =>
	tr.docChanged &&
	!tr.getMeta('isRemote') &&
	!tr.getMeta('replaceDocument') &&
	!isPasteTransaction(tr);

const getSelectedAgentManagedLocalId = (state: EditorState): string | undefined =>
	findAgentManagedExtension(state)?.node.attrs.localId as string | undefined;

const closeConfig = (prev: AgentManagedExtensionPluginState): AgentManagedExtensionPluginState => ({
	...prev,
	autoUpdateIntervalLocalId: undefined,
	configLocalId: undefined,
	isAutoUpdateIntervalOpen: false,
	isConfigOpen: false,
	pendingAutoOpenLocalId: undefined,
});

const openAutoUpdateInterval = (
	prev: AgentManagedExtensionPluginState,
	localId: string | undefined,
): AgentManagedExtensionPluginState => ({
	...prev,
	autoUpdateIntervalLocalId: localId,
	configLocalId: undefined,
	isAutoUpdateIntervalOpen: true,
	isConfigOpen: false,
	pendingAutoOpenLocalId: undefined,
});

const closeAutoUpdateInterval = (
	prev: AgentManagedExtensionPluginState,
): AgentManagedExtensionPluginState => ({
	...prev,
	autoUpdateIntervalLocalId: undefined,
	isAutoUpdateIntervalOpen: false,
});

const toggleAutoUpdateInterval = (
	prev: AgentManagedExtensionPluginState,
	localId: string | undefined,
): AgentManagedExtensionPluginState =>
	prev.isAutoUpdateIntervalOpen && prev.autoUpdateIntervalLocalId === localId
		? closeAutoUpdateInterval(prev)
		: openAutoUpdateInterval(prev, localId);

const openConfigForBlock = (
	prev: AgentManagedExtensionPluginState,
	newState: EditorState,
	localId?: string,
): AgentManagedExtensionPluginState => {
	const target = localId
		? findAgentManagedExtensionByLocalId(newState, localId)
		: findAgentManagedExtension(newState);
	const configLocalId = target?.node.attrs.localId as string | undefined;
	if (!configLocalId) {
		return {
			...prev,
			autoUpdateIntervalLocalId: undefined,
			configLocalId: undefined,
			isAutoUpdateIntervalOpen: false,
			isConfigOpen: false,
		};
	}
	return {
		...prev,
		configLocalId,
		autoUpdateIntervalLocalId: undefined,
		isAutoUpdateIntervalOpen: false,
		isConfigOpen: true,
		pendingAutoOpenLocalId: undefined,
	};
};

const startExecution = (
	prev: AgentManagedExtensionPluginState,
	localId: string,
	requestId: string,
	lifecycleState: AgentManagedBlockLifecycleState,
): AgentManagedExtensionPluginState => {
	const { [localId]: _removedError, ...executionErrorLocalIds } = prev.executionErrorLocalIds;
	const { [localId]: _removedLifecycle, ...executionLifecycleByLocalId } =
		prev.executionLifecycleByLocalId;

	return {
		...prev,
		executionErrorLocalIds,
		executionLifecycleByLocalId: {
			...executionLifecycleByLocalId,
			[localId]: {
				requestId,
				state: lifecycleState,
			},
		},
	};
};

const failExecution = (
	prev: AgentManagedExtensionPluginState,
	localId: string,
	requestId: string,
): AgentManagedExtensionPluginState => {
	if (prev.executionLifecycleByLocalId[localId]?.requestId !== requestId) {
		return prev;
	}

	return {
		...prev,
		executionErrorLocalIds: {
			...prev.executionErrorLocalIds,
			[localId]: true,
		},
	};
};

const finishExecution = (
	prev: AgentManagedExtensionPluginState,
	localId: string,
	requestId: string,
): AgentManagedExtensionPluginState => {
	if (prev.executionLifecycleByLocalId[localId]?.requestId !== requestId) {
		return prev;
	}

	const { [localId]: _removedLifecycle, ...executionLifecycleByLocalId } =
		prev.executionLifecycleByLocalId;
	return {
		...prev,
		executionLifecycleByLocalId,
	};
};

const applyMetaAction = (
	prev: AgentManagedExtensionPluginState,
	meta: AgentManagedExtensionMetaAction,
	newState: EditorState,
): AgentManagedExtensionPluginState => {
	switch (meta.type) {
		case 'CACHE_AI_MANAGED_OBJECT':
			return cacheAiManagedObjectInPluginState(prev, meta.aiManagedObject, meta.localId);
		case 'OPEN_AUTO_UPDATE_INTERVAL':
			return openAutoUpdateInterval(prev, meta.localId);
		case 'CLOSE_AUTO_UPDATE_INTERVAL':
			return closeAutoUpdateInterval(prev);
		case 'TOGGLE_AUTO_UPDATE_INTERVAL':
			return toggleAutoUpdateInterval(prev, meta.localId);
		case 'START_AIMO_LOAD':
			return setAiManagedObjectLoadStatusInPluginState(prev, meta.aimoId, 'loading', meta.localId);
		case 'FAIL_AIMO_LOAD':
			return setAiManagedObjectLoadStatusInPluginState(prev, meta.aimoId, 'failed', meta.localId);
		case 'START_EXECUTION':
			return startExecution(prev, meta.localId, meta.requestId, meta.lifecycleState);
		case 'FAIL_EXECUTION':
			return failExecution(prev, meta.localId, meta.requestId);
		case 'FINISH_EXECUTION':
			return finishExecution(prev, meta.localId, meta.requestId);
		case 'OPEN_CONFIG':
			return openConfigForBlock(prev, newState, meta.localId);
		case 'CLOSE_CONFIG':
			return closeConfig(prev);
		case 'TOGGLE_CONFIG':
			return prev.isConfigOpen ? closeConfig(prev) : openConfigForBlock(prev, newState);
		default:
			return prev;
	}
};

/**
 * Pure reducer for AgentManagedExtensionMeta actions.
 */
export const applyMeta = (
	prev: AgentManagedExtensionPluginState,
	meta: AgentManagedExtensionMeta | undefined,
	newState: EditorState,
): AgentManagedExtensionPluginState => {
	if (!meta) {
		return prev;
	}
	const actions = Array.isArray(meta) ? meta : [meta];
	return actions.reduce((nextState, action) => applyMetaAction(nextState, action, newState), prev);
};

/**
 * Marks a locally inserted agent-managed block for config auto-open.
 * Existing content loaded through document replacement/collab transactions
 * should never auto-open config. Some editor insert flows mark transactions
 * as non-history, so undo history metadata is not a reliable insert signal.
 */
export const applyAutoOpenOnInsert = (
	prev: AgentManagedExtensionPluginState,
	tr: Transaction | ReadonlyTransaction,
	oldState: EditorState,
	newState: EditorState,
): AgentManagedExtensionPluginState => {
	if (!isLocalUserInsertTransaction(tr)) {
		return prev;
	}

	const newLocalIds = findNewLocalIds(oldState, newState);
	if (newLocalIds.length !== 1) {
		return prev;
	}

	return {
		...prev,
		pendingAutoOpenLocalId: newLocalIds[0],
	};
};

/**
 * Returns `{ isConfigOpen: false }` when the popup is open but the
 * selection has moved away from the configured block. Otherwise returns
 * `prev` unchanged.
 */
export const applyAutoCloseOnSelectionChange = (
	prev: AgentManagedExtensionPluginState,
	tr: Transaction | ReadonlyTransaction,
	newState: EditorState,
): AgentManagedExtensionPluginState => {
	if (!tr.selectionSet) {
		return prev;
	}

	const selectedLocalId = getSelectedAgentManagedLocalId(newState);
	let nextState = prev;

	if (
		prev.isAutoUpdateIntervalOpen &&
		(!selectedLocalId || selectedLocalId !== prev.autoUpdateIntervalLocalId)
	) {
		nextState = closeAutoUpdateInterval(nextState);
	}

	if (!nextState.isConfigOpen) {
		return nextState;
	}

	if (selectedLocalId && selectedLocalId === nextState.configLocalId) {
		return nextState;
	}

	return {
		...nextState,
		configLocalId: undefined,
		isConfigOpen: false,
		pendingAutoOpenLocalId: undefined,
	};
};
